import {
  Alert,
  ButtonGroup,
  ButtonProps,
  closeCurrentDialog,
  TabProps,
  Tabs,
} from 'platform/components';
import {Box, Show, VStack} from 'platform/foundation';
import {match} from 'ts-pattern';

import {useState} from 'react';

import {isEmpty, mergeAll, propOr} from 'ramda';
import {isNotNil} from 'ramda-adjunct';

import {useAssignTelephonyCallRecordMutation} from '@dms/api/settings';
import {EntityResourceIds} from '@dms/api/shared';
import i18n from '@dms/i18n';
import {
  DmsDataGrid,
  handleApiError,
  TELEPHONY_CALL_PAIRING_RESOURCES,
  TelephonyCallPairingResource,
} from '@dms/shared';

import {ApiError, buildArray, suffixTestId, TestIdProps} from 'shared/web';

import type {QueryFilterObject} from 'features/eag-grid';

interface LinkCallRecordDialogProps extends TestIdProps {
  callId: string;
  /** The call's customer; the picker lists that customer's records only. */
  customerId: string;
}

const CONFLICT_STATUS = 409;

/** Backend grids linked to the record overviews, multi-select, without row actions. */
const PAIR_GRID_CODES: Record<TelephonyCallPairingResource, string> = {
  [EntityResourceIds.interest]: 'telephony-call-pair-interest',
  [EntityResourceIds.businessCase]: 'telephony-call-pair-business-case',
  [EntityResourceIds.serviceCase]: 'telephony-call-pair-service-case',
  [EntityResourceIds.workshopInterest]: 'telephony-call-pair-workshop-interest',
};

/**
 * Each original grid filters by customer under its own key; service cases only through the
 * vehicles the customer owns, as the customer page does.
 */
const getCustomerFilter = (
  resourceId: TelephonyCallPairingResource,
  customerId: string
): QueryFilterObject =>
  match(resourceId)
    .with(EntityResourceIds.interest, () => ({customer: [customerId]}))
    .with(EntityResourceIds.businessCase, () => ({customerName: [customerId]}))
    .with(EntityResourceIds.serviceCase, () => ({vehicleOwnerships: customerId}))
    .with(EntityResourceIds.workshopInterest, () => ({customer: [customerId]}))
    .exhaustive();

const isCustomerMismatchError = (error: unknown) =>
  propOr(null, 'status', error) === CONFLICT_STATUS;

/**
 * Links records of one kind to a call: a tab per pairable kind, a grid of the customer's records
 * per tab, multi-select, one link request per picked row. Switching tabs drops the picks. A row
 * that belongs to another customer is refused by the backend and reported, the rest link.
 */
export function LinkCallRecordDialog(props: LinkCallRecordDialogProps) {
  const [assignRecord, {isLoading: isLinking}] = useAssignTelephonyCallRecordMutation();
  const [activeTabId, setActiveTabId] = useState<TelephonyCallPairingResource>(
    TELEPHONY_CALL_PAIRING_RESOURCES[0]
  );
  const [selectedRecordIds, setSelectedRecordIds] = useState<string[]>([]);
  const [mismatchCount, setMismatchCount] = useState(0);

  const handleTabChange = (tabId: string) => {
    const resourceId = TELEPHONY_CALL_PAIRING_RESOURCES.find((resource) => resource === tabId);

    if (isNotNil(resourceId)) {
      setActiveTabId(resourceId);
      setSelectedRecordIds([]);
      setMismatchCount(0);
    }
  };

  const handleConfirm = () => {
    setMismatchCount(0);

    // Every row settles before the dialog decides; a failed row resolves to its error.
    Promise.all(
      selectedRecordIds.map((recordId) =>
        assignRecord({callId: props.callId, body: {resourceId: activeTabId, recordId}})
          .unwrap()
          .then(() => null)
          .catch((error: ApiError) => error)
      )
    ).then((results) => {
      const failures = results.filter(isNotNil);
      const mismatches = failures.filter(isCustomerMismatchError);
      const otherFailure = failures.find((failure) => !isCustomerMismatchError(failure));

      if (isNotNil(otherFailure)) {
        handleApiError(otherFailure);
      }

      if (isEmpty(failures)) {
        closeCurrentDialog();
        return;
      }

      setMismatchCount(mismatches.length);
    });
  };

  const tabs: TabProps[] = TELEPHONY_CALL_PAIRING_RESOURCES.map((resourceId) => ({
    id: resourceId,
    title: i18n.t(`entity.customerMessage.labels.contextResource.${resourceId}`),
    content: (
      <Box height="100%">
        <DmsDataGrid
          gridCode={PAIR_GRID_CODES[resourceId]}
          queryModifier={(filter) =>
            mergeAll([filter, getCustomerFilter(resourceId, props.customerId)])
          }
          onRowSelectionChange={(rows) => setSelectedRecordIds(rows.map((row) => row.id))}
          data-testid={suffixTestId(`grid-${resourceId}`, props)}
        />
      </Box>
    ),
  }));

  return (
    <VStack height="100%" spacing={4}>
      <Show when={mismatchCount > 0}>
        <Alert
          variant="warning"
          title={i18n.t('page.telephony.callDetail.linkCustomerMismatch', {count: mismatchCount})}
          data-testid={suffixTestId('mismatch', props)}
        />
      </Show>
      <Box flex={1} minHeight={0}>
        <Tabs
          isFullHeight
          variant="condensed"
          tabs={tabs}
          activeTabId={activeTabId}
          onChange={handleTabChange}
          lazyBehavior="unmount"
          isLazy
          data-testid={suffixTestId('tabs', props)}
        />
      </Box>
      <ButtonGroup
        align="right"
        buttons={buildArray<ButtonProps>()
          .add({
            title: i18n.t('general.actions.cancel'),
            onClick: closeCurrentDialog,
            variant: 'secondary',
            'data-testid': suffixTestId('cancel', props),
          })
          .add({
            title: i18n.t('page.telephony.callDetail.linkRecords', {
              count: selectedRecordIds.length,
            }),
            onClick: handleConfirm,
            isDisabled: isEmpty(selectedRecordIds),
            isLoading: isLinking,
            'data-testid': suffixTestId('confirm', props),
          })}
      />
    </VStack>
  );
}
